import { CSSResultGroup, html, LitElement } from 'lit'
import { customElement, property, queryAssignedElements, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './accordion.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'
import { generateID } from 'elements/_shared/generate-id.js'
import { IconSize } from 'elements/icon/icon.js'

export const accordionVariants = ['default', 'panel', 'simple'] as const
export type accordionVariantsTypes = typeof accordionVariants

export class AccordionToggleEvent extends Event {
  static type = 'accordion-toggle' as const

  accordion: CSEAccordion

  constructor(accordion: CSEAccordion) {
    super(AccordionToggleEvent.type, { bubbles: true, composed: true })
    this.accordion = accordion
  }
}

initializeIconsByRole({
  'cse-accordion.closed': { name: 'angle-down', style: 'light' },
  'cse-accordion.open': { name: 'angle-down', style: 'light' },
  'cse-accordion.toggle': { name: 'angle-down', style: 'light' },
  'cse-accordion.plus': { name: 'plus', style: 'light' },
  'cse-accordion.minus': { name: 'minus', style: 'light' },
  'cse-accordion.long-arrow-up': { name: 'long-arrow-up', style: 'light' },
  'cse-accordion.long-arrow-down': { name: 'long-arrow-down', style: 'light' },

})

@customElement('cse-accordion')
export class CSEAccordion extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, fontStyles, style]

  @property({ type: Boolean })
  open = false

  @property({ type: String })
  variant: accordionVariantsTypes[number] = 'default'

  /** @deprecated Remap the `cse-accordion.closed` / `cse-accordion.open` icon roles and set `--cse-accordion-icon-rotate-open` instead. */
  @property({ type: String, attribute: 'icon-role', reflect: true })
  iconRole: string = 'toggle'

  @property({ type: String, attribute: 'size' })
  size: IconSize = 'md'

  @state()
  private hasClosedIcon = false

  @state()
  private hasOpenIcon = false

  private contentId = generateID('accordion')

  override willUpdate(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('iconRole') && this.iconRole !== 'toggle') {
      console.warn('<cse-accordion> "icon-role" is deprecated, remap the "cse-accordion.closed" and "cse-accordion.open" icon roles and set "--cse-accordion-icon-rotate-open" instead.')
    }
  }


  override updated(changedProperties: Map<string, unknown>) {
    // Update details element when open property changes (e.g., from accordion group)
    if (changedProperties.has('open')) {
      const detailsElement = this.querySelector('details') as HTMLDetailsElement
      if (detailsElement && detailsElement.open !== this.open) {
        detailsElement.open = this.open
      }
    }
  }

  private handleDetailsToggle = (event: Event) => {
    const details = event.target as HTMLDetailsElement
    this.open = details.open
    this.dispatchEvent(new AccordionToggleEvent(this))
  }

  private getIconRole = (): string => {
    if (this.iconRole === 'toggle') {
      return this.open ? 'cse-accordion.open' : 'cse-accordion.closed'
    }
    if (this.iconRole === 'plus') {
      return this.open ? 'cse-accordion.minus' : 'cse-accordion.plus'
    }
    return `cse-accordion.${this.iconRole}`
  }

  private handleIconSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement
    const hasIcon = slot.assignedElements().length > 0
    if (slot.name === 'icon-closed') this.hasClosedIcon = hasIcon
    if (slot.name === 'icon-open') this.hasOpenIcon = hasIcon
  }

  private renderIcon(defaultIcon: unknown) {
    const showOpenIcon = this.open && this.hasOpenIcon
    return html`
      ${this.hasClosedIcon || this.hasOpenIcon ? '' : defaultIcon}
      <slot name="icon-closed" class="${classMap({ hidden: showOpenIcon })}" @slotchange=${this.handleIconSlotChange}></slot>
      <slot name="icon-open" class="${classMap({ hidden: !showOpenIcon })}" @slotchange=${this.handleIconSlotChange}></slot>
    `
  }

  private getIconClasses = () => {
    const baseClasses = {
      'icon': true,
      'open': this.open,
      'no-rotate': this.iconRole === 'plus'
    }

    return classMap(baseClasses)
  }

  override render() {
    const renderSummary = () => {
      if (this.variant === 'simple') {
        return html`
        <summary class="header font-heading-3">
          <slot name="title"></slot>

            <cse-icon-button  size="small" variant="secondary">
              ${this.renderIcon(html`<cse-icon class="${this.getIconClasses()}" icon-role="${this.getIconRole()}" size="xs" light></cse-icon>`)}
            </cse-icon-button>

        </summary>
        `
      } else {
        return html`
          <summary class="header font-heading-4">
            <slot name="title"></slot>
            ${this.renderIcon(html`<cse-icon class="${this.getIconClasses()}" icon-role="${this.getIconRole()}" .size=${this.size}></cse-icon>`)}
          </summary>
        `
      }
    }

    return html`
      <details
        class="${classMap({
          'cse-accordion': true,
          'open': this.open,
          [`variant-${this.variant}`]: true,
        })}"
        .open=${this.open}
        @toggle=${this.handleDetailsToggle}
      >
        ${renderSummary()}
        <div class="content font-body" id=${this.contentId}>
          <slot name="content"></slot>
        </div>
      </details>
    `
  }
}


@customElement('cse-accordion-group')
export class CSEAccordionGroup extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Boolean, attribute: 'multiopen' })
  multiOpen = false

  @queryAssignedElements()
  slottedElements!: HTMLElement[]

  private isCSEAccordion = (element: HTMLElement): element is CSEAccordion => {
    return element instanceof CSEAccordion
  }

  public handleAccordionToggle(event: AccordionToggleEvent) {
    if (this.multiOpen) {
      return
    }

    this.slottedElements
      .filter(this.isCSEAccordion)
      .forEach(accordion => {


        if (event.accordion.open) {

          accordion.open = accordion === event.accordion
        }

      })
  }

  override render() {
    return html`
      <div class="cse-accordion-group" @accordion-toggle=${this.handleAccordionToggle}>
        <slot></slot>
      </div>
    `
  }
}


declare global {
  interface HTMLElementTagNameMap {
    'cse-accordion': CSEAccordion
    'cse-accordion-group': CSEAccordionGroup
  }
}
